export const INDICATOR_KEYS = [
  "btc_usd",
  "btc_dominance",
  "eth_dominance",
  "stable_dominance",
  "total3_usd",
  "fng",
  "alt_season",
  "eth_btc",
  "btc_200w",
  "btc_pi_cycle",
  "btc_mvrv",
  "halving_days",
] as const;

export type IndicatorKey = (typeof INDICATOR_KEYS)[number];

export type IndicatorMeta = {
  readonly key: IndicatorKey;
  readonly name: string;
  readonly unit: string;
  /** short label used in conditions / alerts, e.g. "F&G" */
  readonly short: string;
  readonly decimals: number;
  /** change is expressed in percentage points, not percent */
  readonly isPp: boolean;
  /** format value as 0.94 T / 2.1 B */
  readonly big: boolean;
  readonly hint: string;
};

export const INDICATORS: readonly IndicatorMeta[] = [
  {
    key: "btc_usd",
    name: "BTC",
    unit: "USD",
    short: "BTC",
    decimals: 0,
    isPp: false,
    big: false,
    hint: "Baza wszystkiego. Gdy 7d i 30d na minusie — alty spadają mocniej.",
  },
  {
    key: "btc_dominance",
    name: "Dominacja BTC",
    unit: "BTC.D %",
    short: "BTC.D",
    decimals: 1,
    isPp: true,
    big: false,
    hint: "Rośnie = kapitał ucieka do BTC, alty słabe.",
  },
  {
    key: "eth_dominance",
    name: "Dominacja ETH",
    unit: "ETH.D %",
    short: "ETH.D",
    decimals: 1,
    isPp: true,
    big: false,
    hint: "Proxy siły altów L1.",
  },
  {
    key: "stable_dominance",
    name: "Dominacja stablecoinów",
    unit: "USDT.D + USDC.D %",
    short: "USDT.D",
    decimals: 1,
    isPp: true,
    big: false,
    hint: "Rośnie = risk-off, kapitał czeka na boku.",
  },
  {
    key: "total3_usd",
    name: "TOTAL3",
    unit: "USD, bez BTC i ETH",
    short: "TOTAL3",
    decimals: 0,
    isPp: false,
    big: true,
    hint: "To jest Twój rynek — kapitalizacja altów bez BTC i ETH.",
  },
  {
    key: "fng",
    name: "Fear & Greed",
    unit: "0–100",
    short: "F&G",
    decimals: 0,
    isPp: true,
    big: false,
    hint: "Poniżej 30 = strach (start DCA), powyżej 75 = chciwość (TP, nie kupno).",
  },
  {
    key: "alt_season",
    name: "Altcoin Season Index",
    unit: "% top-50 bijących BTC, 90 dni",
    short: "ASI",
    decimals: 0,
    isPp: true,
    big: false,
    hint: "Poniżej 25 = BTC season, powyżej 75 = altseason. Liczony lokalnie.",
  },
  {
    key: "eth_btc",
    name: "ETH / BTC",
    unit: "ratio",
    short: "ETH/BTC",
    decimals: 4,
    isPp: false,
    big: false,
    hint: "Klasyczny sygnał altseason — rośnie, gdy alty mają paliwo.",
  },
  {
    key: "btc_200w",
    name: "BTC vs 200W SMA",
    unit: "× SMA 200 tyg.",
    short: "200W",
    decimals: 2,
    isPp: false,
    big: false,
    hint: "Poniżej 1 = cena pod 200-tygodniową średnią (historyczne dno cyklu). Powyżej 3 = strefa szczytu.",
  },
  {
    key: "btc_pi_cycle",
    name: "Pi Cycle Top",
    unit: "111DMA / 2×350DMA",
    short: "PI",
    decimals: 2,
    isPp: false,
    big: false,
    hint: "Zbliża się do 1 = historycznie szczyt cyklu BTC. Poniżej 0,7 = spokój.",
  },
  {
    key: "btc_mvrv",
    name: "BTC MVRV",
    unit: "ratio (Coin Metrics)",
    short: "MVRV",
    decimals: 2,
    isPp: false,
    big: false,
    hint: "Poniżej 1 = rynek pod kosztem (dno), powyżej 3 = przegrzanie.",
  },
  {
    key: "halving_days",
    name: "Dni od halvingu",
    unit: "dni",
    short: "HALVING",
    decimals: 0,
    isPp: true,
    big: false,
    hint: "Szczyty cyklu historycznie 12–18 mies. (≈ 365–550 dni) po halvingu. Ostatni: 20.04.2024.",
  },
];

export function indicatorMeta(key: IndicatorKey): IndicatorMeta {
  const meta = INDICATORS.find((i) => i.key === key);
  if (!meta) throw new Error(`Unknown indicator: ${key}`);
  return meta;
}
